// PII-scrubbing logger. Forwards to Sentry; falls back to console.

import * as Sentry from '@sentry/react';

const PII_KEYS = new Set(['email', 'phone', 'password', 'token', 'api_key', 'authorization']);
const EMAIL_RE = /[\w.+-]+@[\w-]+\.[\w.-]+/g;
const PHONE_RE = /\b(?:\+?\d[\d\s().-]{7,}\d)\b/g;

function scrubValue(v: unknown): unknown {
  if (typeof v === 'string') {
    return v.replace(EMAIL_RE, '<redacted-email>').replace(PHONE_RE, '<redacted-phone>');
  }
  if (Array.isArray(v)) return v.map(scrubValue);
  if (v && typeof v === 'object') return scrubObject(v as Record<string, unknown>);
  return v;
}

export function scrubObject(input: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(input)) {
    if (PII_KEYS.has(k.toLowerCase())) out[k] = '<redacted>';
    else out[k] = scrubValue(v);
  }
  return out;
}

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

const LEVEL_TO_SENTRY: Record<LogLevel, Sentry.SeverityLevel> = {
  debug: 'debug',
  info: 'info',
  warn: 'warning',
  error: 'error',
};

function emit(level: LogLevel, msg: string, ctx?: Record<string, unknown>) {
  const safe = ctx ? scrubObject(ctx) : undefined;
  const payload = safe ? { msg, ...safe } : { msg };

  // Console (dev visibility)
  switch (level) {
    case 'debug':
      if (import.meta.env.DEV) console.debug(payload);
      break;
    case 'info':
      console.info(payload);
      break;
    case 'warn':
      console.warn(payload);
      break;
    case 'error':
      console.error(payload);
      break;
  }

  // Sentry — breadcrumbs for everything (cheap, no quota cost), captureMessage on error.
  Sentry.addBreadcrumb({
    category: 'app',
    level: LEVEL_TO_SENTRY[level],
    message: msg,
    data: safe,
  });
  if (level === 'error') {
    Sentry.captureMessage(msg, { level: 'error', extra: safe });
  }
}

// Emits a Sentry breadcrumb for a discrete event.
export function track(category: string, message: string, data?: Record<string, unknown>) {
  Sentry.addBreadcrumb({
    category,
    level: 'info',
    message,
    data: data ? scrubObject(data) : undefined,
  });
}

export const log = {
  debug: (msg: string, ctx?: Record<string, unknown>) => emit('debug', msg, ctx),
  info: (msg: string, ctx?: Record<string, unknown>) => emit('info', msg, ctx),
  warn: (msg: string, ctx?: Record<string, unknown>) => emit('warn', msg, ctx),
  error: (msg: string, ctx?: Record<string, unknown>) => emit('error', msg, ctx),
};
